import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import client from '../../../lib/feathers';
import toast from 'react-hot-toast';
import {
  faPencil,
  faTrash,
  faPlus,
  faCheck,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const VintageFormats = ({ libationVintageId }) => {
  const user = useSelector((state) => state.auth.user);
  // Role-scoped so this works on the review screen for industry users too
  // (industry can't hit the admin/ service).
  const servicePath = `${user.role}/libation-vintage-format`;
  const [vintageFormats, setVintageFormats] = useState([]);
  const [formats, setFormats] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newFormatId, setNewFormatId] = useState('');
  const [newUpc, setNewUpc] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [editUpc, setEditUpc] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const loadFormats = async () => {
    try {
      const formatService = client.service('client/format');
      const result = await formatService.find({ query: { $sort: { sortOrder: 1 }, $limit: 50 } });
      setFormats(result.data || result);
    } catch (err) {
      console.error('Error loading formats:', err);
    }
  };

  const loadVintageFormats = async () => {
    try {
      const service = client.service(servicePath);
      const result = await service.find({
        query: { libationVintageId, $sort: { createdAt: 1 }, $limit: 50 }
      });
      setVintageFormats(result.data || result);
    } catch (err) {
      console.error('Error loading vintage formats:', err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (libationVintageId) {
      loadFormats();
      loadVintageFormats();
    }
  }, [libationVintageId]);

  // Formats not yet assigned to this vintage
  const availableFormats = formats.filter(
    (f) => !vintageFormats.some((vf) => vf.formatId === f.id)
  );

  const formatDisplay = (f) => `${f.name} (${f.volumeMl}ml)`;

  const handleAdd = async () => {
    if (!newFormatId) return;
    try {
      const service = client.service(servicePath);
      await service.create({
        libationVintageId,
        formatId: newFormatId,
        upc: newUpc || null,
        price: newPrice || null,
      });
      toast.success('Format added');
      setIsAdding(false);
      setNewFormatId('');
      setNewUpc('');
      setNewPrice('');
      await loadVintageFormats();
    } catch (err) {
      toast.error(err.message || 'Failed to add format');
    }
  };

  const handleStartEdit = (vf) => {
    setEditingId(vf.id);
    setEditUpc(vf.upc || '');
    setEditPrice(vf.price || '');
  };

  const handleSaveEdit = async (id) => {
    try {
      const service = client.service(servicePath);
      await service.patch(id, {
        upc: editUpc || null,
        price: editPrice || null,
      });
      toast.success('Format updated');
      setEditingId(null);
      await loadVintageFormats();
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleDelete = async (vf) => {
    const formatName = vf.format ? formatDisplay(vf.format) : 'this format';
    if (!window.confirm(`Remove ${formatName} from this vintage?`)) return;
    try {
      const service = client.service(servicePath);
      await service.remove(vf.id);
      toast.success('Format removed');
      await loadVintageFormats();
    } catch (err) {
      toast.error(err.message || 'Failed to remove');
    }
  };

  if (isLoading) return null;

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Formats &amp; UPC</h3>
          <p className="mt-1 text-sm text-gray-500">
            Available packaging formats for this vintage. Add UPC barcodes for quick scanning.
          </p>
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          {/* Existing formats */}
          <div className="space-y-2">
            {vintageFormats.map((vf) => (
              <div key={vf.id} className="flex items-center gap-3 p-3 border rounded-md bg-gray-50">
                {editingId === vf.id ? (
                  /* Edit mode */
                  <>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {vf.format ? formatDisplay(vf.format) : 'Unknown Format'}
                      </div>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="text"
                          value={editUpc}
                          onChange={(e) => setEditUpc(e.target.value)}
                          placeholder="UPC barcode"
                          maxLength={14}
                          className="block w-40 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                        />
                        <div className="relative">
                          <span className="absolute flex inset-y-0 left-2 items-center text-sm text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            placeholder="Price"
                            className="block w-24 pl-5 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                          />
                        </div>
                      </div>
                    </div>
                    <button onClick={() => handleSaveEdit(vf.id)} className="text-green-600 hover:text-green-800 p-1">
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600 p-1">
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </>
                ) : (
                  /* Display mode */
                  <>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {vf.format ? formatDisplay(vf.format) : 'Unknown Format'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {vf.upc && <span className="mr-3">UPC: {vf.upc}</span>}
                        {vf.price && <span>MSRP: ${parseFloat(vf.price).toFixed(2)}</span>}
                        {!vf.upc && !vf.price && <span className="italic">No UPC or price set</span>}
                      </div>
                    </div>
                    <button onClick={() => handleStartEdit(vf)} className="text-gray-400 hover:text-gray-600 p-1">
                      <FontAwesomeIcon icon={faPencil} size="sm" />
                    </button>
                    <button onClick={() => handleDelete(vf)} className="text-gray-400 hover:text-red-600 p-1">
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </button>
                  </>
                )}
              </div>
            ))}

            {vintageFormats.length === 0 && !isAdding && (
              <p className="text-sm text-gray-400 italic">No formats added yet.</p>
            )}
          </div>

          {/* Add new format */}
          {isAdding ? (
            <div className="mt-3 p-3 border-2 border-dashed border-gray-300 rounded-md">
              <div className="flex flex-col gap-2">
                <select
                  value={newFormatId}
                  onChange={(e) => setNewFormatId(e.target.value)}
                  className="block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                >
                  <option value="">Select format...</option>
                  {availableFormats.map((f) => (
                    <option key={f.id} value={f.id}>{formatDisplay(f)}</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newUpc}
                    onChange={(e) => setNewUpc(e.target.value)}
                    placeholder="UPC barcode (optional)"
                    maxLength={14}
                    className="block flex-1 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                  />
                  <div className="relative">
                    <span className="absolute flex inset-y-0 left-2 items-center text-sm text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      placeholder="Price"
                      className="block w-24 pl-5 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setIsAdding(false); setNewFormatId(''); setNewUpc(''); setNewPrice(''); }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAdd}
                    disabled={!newFormatId}
                    className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Format
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setIsAdding(true)}
              disabled={availableFormats.length === 0}
              className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 disabled:text-gray-300 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faPlus} size="sm" />
              Add Format
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VintageFormats;
